import {
  AtSign,
  MessageCircle,
  Pencil,
  Phone,
  Plus,
  Send,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import {
  useCreate,
  useDelete,
  useGetIdentity,
  useGetList,
  useNotify,
  useUpdate,
  type Identifier,
  type UserIdentity,
} from "ra-core";
import { useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { CompanyPrivateContact } from "../types";

type AdminIdentity = UserIdentity & { administrator?: boolean };

type ContactDraft = {
  nickname: string;
  phone: string;
  telegram: string;
  max_contact: string;
  whatsapp: string;
};

const emptyDraft: ContactDraft = {
  nickname: "",
  phone: "",
  telegram: "",
  max_contact: "",
  whatsapp: "",
};

const toDraft = (contact?: CompanyPrivateContact | null): ContactDraft => ({
  nickname: contact?.nickname || "",
  phone: contact?.phone || "",
  telegram: contact?.telegram || "",
  max_contact: contact?.max_contact || "",
  whatsapp: contact?.whatsapp || "",
});

const trimOrNull = (value: string) => value.trim() || null;

const toTelegramUrl = (value: string) => {
  if (/^https?:\/\//i.test(value)) return value;
  const handle = value.trim().replace(/^@/, "");
  return handle ? `https://t.me/${handle}` : undefined;
};

const toWhatsappUrl = (value: string) => {
  if (/^https?:\/\//i.test(value)) return value;
  const phone = value.replace(/\D/g, "");
  return phone ? `https://wa.me/${phone}` : undefined;
};

const toMaxUrl = (value: string) =>
  /^https?:\/\//i.test(value) ? value : undefined;

const toPhoneUrl = (value: string) => {
  const phone = value.replace(/[^\d+]/g, "");
  return phone ? `tel:${phone}` : undefined;
};

const PrivateContactValue = ({
  icon: Icon,
  label,
  value,
  href,
}: {
  icon: typeof Phone;
  label: string;
  value?: string | null;
  href?: string;
}) => {
  if (!value) return null;

  const content = (
    <>
      <Icon className="size-4 shrink-0 text-zinc-400" />
      <span className="min-w-0 flex-1 break-all text-sm text-white">
        {value}
      </span>
    </>
  );

  return href ? (
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
      className="flex min-h-8 items-center gap-2 rounded-md px-2 py-1 transition-colors hover:bg-white/10"
      aria-label={`${label}: ${value}`}
    >
      {content}
    </a>
  ) : (
    <div
      className="flex min-h-8 items-center gap-2 rounded-md px-2 py-1"
      aria-label={`${label}: ${value}`}
    >
      {content}
    </div>
  );
};

export const CompanyPrivateContacts = ({
  companyId,
}: {
  companyId: Identifier;
}) => {
  const { identity, isPending: identityPending } = useGetIdentity();
  const isAdmin =
    (identity as AdminIdentity | undefined)?.administrator === true;
  const notify = useNotify();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CompanyPrivateContact | null>(null);
  const [draft, setDraft] = useState<ContactDraft>(emptyDraft);

  const {
    data: contacts = [],
    isPending,
    refetch,
  } = useGetList<CompanyPrivateContact>(
    "company_private_contacts",
    {
      filter: { company_id: companyId },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "created_at", order: "ASC" },
    },
    { enabled: isAdmin && !!companyId },
  );
  const [create, { isPending: isCreating }] = useCreate();
  const [update, { isPending: isUpdating }] = useUpdate();
  const [deleteOne, { isPending: isDeleting }] = useDelete();

  if (identityPending || !isAdmin) return null;

  const openCreate = () => {
    setEditing(null);
    setDraft(emptyDraft);
    setDialogOpen(true);
  };

  const openEdit = (contact: CompanyPrivateContact) => {
    setEditing(contact);
    setDraft(toDraft(contact));
    setDialogOpen(true);
  };

  const updateDraft = (field: keyof ContactDraft, value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const hasAnyValue = Object.values(draft).some((value) => value.trim());
  const saving = isCreating || isUpdating;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasAnyValue) return;

    const data = {
      company_id: companyId,
      nickname: trimOrNull(draft.nickname),
      phone: trimOrNull(draft.phone),
      telegram: trimOrNull(draft.telegram),
      max_contact: trimOrNull(draft.max_contact),
      whatsapp: trimOrNull(draft.whatsapp),
      updated_at: new Date().toISOString(),
    };

    try {
      if (editing) {
        await update(
          "company_private_contacts",
          { id: editing.id, data, previousData: editing },
          { returnPromise: true },
        );
      } else {
        await create(
          "company_private_contacts",
          { data },
          { returnPromise: true },
        );
      }
      await refetch();
      setDialogOpen(false);
      notify("Специальный контакт сохранён", { type: "success" });
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Не удалось сохранить контакт",
        { type: "error" },
      );
    }
  };

  const handleDelete = async (contact: CompanyPrivateContact) => {
    if (!window.confirm("Удалить этот специальный контакт?")) return;
    try {
      await deleteOne(
        "company_private_contacts",
        { id: contact.id, previousData: contact },
        { returnPromise: true },
      );
      await refetch();
      notify("Специальный контакт удалён", { type: "success" });
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Не удалось удалить контакт",
        { type: "error" },
      );
    }
  };

  return (
    <>
      <section className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 text-white shadow-sm">
        <div className="flex items-start justify-between gap-3 border-b border-white/10 px-4 py-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ShieldCheck className="size-4 text-zinc-300" />
              <h3 className="font-semibold tracking-tight">
                Специальные контакты
              </h3>
            </div>
            <p className="mt-1 text-xs text-zinc-400">
              Видно только администраторам
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={openCreate}
            className="border-white/20 bg-white/5 text-white hover:bg-white/15 hover:text-white"
          >
            <Plus />
            Добавить
          </Button>
        </div>

        <div className="space-y-2 p-3">
          {isPending ? (
            <p className="px-2 py-3 text-sm text-zinc-400">Загрузка…</p>
          ) : contacts.length === 0 ? (
            <p className="px-2 py-3 text-sm text-zinc-400">
              Специальных контактов пока нет
            </p>
          ) : (
            contacts.map((contact) => (
              <article
                key={contact.id}
                className="rounded-lg border border-white/10 bg-white/[0.04] p-2"
              >
                <div className="flex items-center justify-between gap-2 px-2 py-1">
                  <div className="min-w-0 font-medium text-white">
                    {contact.nickname || "Контакт без имени"}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(contact)}
                      className="size-7 text-zinc-300 hover:bg-white/10 hover:text-white"
                      aria-label="Редактировать специальный контакт"
                    >
                      <Pencil />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={isDeleting}
                      onClick={() => handleDelete(contact)}
                      className="size-7 text-zinc-400 hover:bg-red-500/15 hover:text-red-300"
                      aria-label="Удалить специальный контакт"
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </div>
                <PrivateContactValue
                  icon={Phone}
                  label="Телефон"
                  value={contact.phone}
                  href={contact.phone ? toPhoneUrl(contact.phone) : undefined}
                />
                <PrivateContactValue
                  icon={Send}
                  label="Telegram"
                  value={contact.telegram}
                  href={
                    contact.telegram
                      ? toTelegramUrl(contact.telegram)
                      : undefined
                  }
                />
                <PrivateContactValue
                  icon={AtSign}
                  label="MAX"
                  value={contact.max_contact}
                  href={
                    contact.max_contact
                      ? toMaxUrl(contact.max_contact)
                      : undefined
                  }
                />
                <PrivateContactValue
                  icon={MessageCircle}
                  label="WhatsApp"
                  value={contact.whatsapp}
                  href={
                    contact.whatsapp
                      ? toWhatsappUrl(contact.whatsapp)
                      : undefined
                  }
                />
              </article>
            ))
          )}
        </div>
      </section>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <form onSubmit={handleSubmit} className="space-y-5">
            <DialogHeader>
              <DialogTitle>
                {editing
                  ? "Редактировать специальный контакт"
                  : "Добавить специальный контакт"}
              </DialogTitle>
              <DialogDescription>
                Эти данные доступны только администраторам CRM.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 sm:grid-cols-2">
              <PrivateContactInput
                id="private-contact-nickname"
                label="Никнейм или имя"
                value={draft.nickname}
                onChange={(value) => updateDraft("nickname", value)}
                placeholder="Например, Иван или @nickname"
              />
              <PrivateContactInput
                id="private-contact-phone"
                label="Телефон"
                value={draft.phone}
                onChange={(value) => updateDraft("phone", value)}
                placeholder="+7 900 000-00-00"
                inputMode="tel"
              />
              <PrivateContactInput
                id="private-contact-telegram"
                label="Telegram"
                value={draft.telegram}
                onChange={(value) => updateDraft("telegram", value)}
                placeholder="@username или ссылка"
              />
              <PrivateContactInput
                id="private-contact-max"
                label="MAX"
                value={draft.max_contact}
                onChange={(value) => updateDraft("max_contact", value)}
                placeholder="Никнейм или ссылка"
              />
              <PrivateContactInput
                id="private-contact-whatsapp"
                label="WhatsApp"
                value={draft.whatsapp}
                onChange={(value) => updateDraft("whatsapp", value)}
                placeholder="Телефон или ссылка"
                inputMode="tel"
                className="sm:col-span-2"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Отмена
              </Button>
              <Button type="submit" disabled={!hasAnyValue || saving}>
                {saving ? "Сохранение…" : "Сохранить"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

const PrivateContactInput = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  inputMode,
  className,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  inputMode?: "tel";
  className?: string;
}) => (
  <div className={`space-y-2 ${className || ""}`}>
    <Label htmlFor={id}>{label}</Label>
    <Input
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      inputMode={inputMode}
      autoComplete="off"
    />
  </div>
);
